// App do motorista. Ele entra pelo link pessoal que o titular (ou o
// escritório) manda pelo WhatsApp, sem e-mail e sem senha, e vê só o que é
// dele: as viagens, as cargas com a comissão, os vales e o saldo até hoje.
// Completa os dados da carga que o escritório não lançou (CT-e, peso…),
// lança as despesas da viagem com a foto da nota (ou diz por que não tem
// nota) e chama o SOS mecânica.
import {
  acertos, anexos, cargaEntregue, categorias, clientes, colaboradores, dadosDaPrevia, despesas, entregas, entrarPeloLink, eventosDaViagem, fretes,
  linkJaUsadoAqui, manutencao, paradaEntregue, situacoesDeEnvio, vales, veiculos, viagens,
} from '../dados.js';
import { h, icone, trocar } from '../lib/dom.js';
import { detalheDoErro, mensagemDeErro } from '../lib/erros.js';
import { lugar, paradasDaCarga, situacaoDosCaminhoes, textoDaRota, textoDaSituacao, textoDoMomento } from '../lib/entregas.js';
import { data, dinheiro, lerInteiro, lerNumero, litros as formatarLitros, numeroBR } from '../lib/formato.js';
import {
  CAMPOS_DA_CARGA, MOTIVOS_SEM_NOTA, abertoPeloIcone, ehIphone, faltaDadoNaCarga, ganhosDaCarga, motivoSemNota, motoristaMexe, saldoDoMotorista,
  textoDoGanho,
} from '../lib/motorista.js';
import { UFS } from '../lib/validacao.js';
import { ir, nestaTela, renderizar } from '../rotas.js';
import { contaGravavel, ehAgenciador, ehMotorista, entrouSemEmail, hojeDaEmpresa, motoristaGrava, prepararUsuario, sair, sessao } from '../sessao.js';
import { casca } from '../ui/casca.js';
import { avisar, campo, carregando, cartaoLink, confirmar, dialogo, emblema, faixa, formulario, secao, vazio } from '../ui/componentes.js';
import { botoesDeFoto, miniaturaDaPrevia, miniaturaDoAnexo } from '../ui/fotos.js';
import { botaoDeLerNota } from '../ui/nota-ia.js';
import { leitorDaChave } from '../ui/chave-do-cte.js';
import { emblemaEnvio } from '../ui/sinal.js';
import { avisosDoMotorista, blocoManutencaoFeita } from './manutencao.js';
import { opcoesDeCategoria, rotular } from './despesas.js';
import { FORMAS_PAGAMENTO } from './pagar.js';
import { marca } from './entrada.js';
import { STATUS, cartaoViagem, textoCaminhao } from './viagens.js';
import { linkDoTrajeto } from './raiox.js';
import { blocoModoViagem } from './modo-viagem.js';

const nomeDaEmpresa = () => sessao.empresa?.nome_fantasia || sessao.empresa?.nome || 'a empresa';
const opcoesUf = [['', 'UF'], ...UFS.map((u) => [u, u])];

// ---------------------------------------------------------------------
// Entrada pelo link: #/motorista/<código> ou #/agenciador/<código> (0.21.0)
// ---------------------------------------------------------------------
function mensagemDoLink(erro) {
  const texto = String(erro?.message ?? '');
  if (/anonymous sign-ins are disabled/i.test(texto)) {
    return 'O app ainda não está ligado no servidor. Avise o dono da frota.';
  }
  return mensagemDeErro(erro);
}

const TEXTOS_DO_LINK = {
  motorista: {
    titulo: 'App do motorista',
    subtitulo: 'Este link liga o seu celular às viagens, aos vales e às despesas da empresa.',
    deQuem: 'do motorista',
  },
  agenciador: {
    titulo: 'App do agenciador',
    subtitulo: 'Este link liga o seu celular às cargas que você agenciou para a empresa.',
    deQuem: 'do agenciador',
  },
};

// No iPhone, o app posto na Tela de Início não enxerga o que foi feito no
// Safari: o link só é usado depois, já dentro do app (por "Sou motorista",
// colando o link). Quem preferir usa pelo Safari mesmo.
function passosDoIphone(lugar, entrar) {
  trocar(lugar, h('div', { class: 'cartao-claro' },
    h('h2', null, 'No iPhone, são 3 passos'),
    h('ol', { class: 'passos-iphone' },
      h('li', null, 'Toque em ', h('strong', null, 'Copiar o link'), '.',
        h('button', {
          class: 'botao cheio',
          type: 'button',
          onClick: async () => {
            try {
              await navigator.clipboard.writeText(location.href);
              avisar('Link copiado.');
            } catch {
              avisar('Não deu para copiar aqui: no WhatsApp, toque e segure no link e escolha Copiar.', 'erro');
            }
          },
        }, 'Copiar o link')),
      h('li', null, 'Toque em ', h('strong', null, 'Compartilhar'), ' (o quadrado com a seta para cima, na barra do Safari), depois em ',
        h('strong', null, 'Adicionar à Tela de Início'), ' e em ', h('strong', null, 'Adicionar'), '.'),
      h('li', null, 'Abra o ', h('strong', null, 'HSA Frota'), ' pelo ícone novo, toque em ', h('strong', null, 'Sou motorista ou agenciador'),
        ' e em ', h('strong', null, 'Colar o link'), '.')),
    h('p', { class: 'pequeno' }, 'Se não aparecer "Adicionar à Tela de Início", abra este link no Safari.'),
    h('button', { class: 'botao secundario cheio', type: 'button', onClick: () => entrar() }, 'Usar pelo Safari, sem instalar')));
}

export const telaEntrarMotorista = (opcoes) => telaEntrarPeloLink({ ...opcoes, quem: 'motorista' });
export const telaEntrarAgenciador = (opcoes) => telaEntrarPeloLink({ ...opcoes, quem: 'agenciador' });

function telaEntrarPeloLink({ params, quem }) {
  const textos = TEXTOS_DO_LINK[quem];
  const lugar = h('div', { class: 'secao', dataset: { atualizaSozinha: '1' } }, carregando('Ligando este celular ao HSA Frota…'));
  const pagina = h('div', { class: 'entrada' },
    marca(),
    h('div', null, h('h1', null, textos.titulo), h('p', { class: 'subtitulo' }, textos.subtitulo)),
    lugar);

  async function entrar() {
    trocar(lugar, carregando('Ligando este celular ao HSA Frota…'));
    try {
      const { usuario, papel } = await entrarPeloLink(params.codigo);
      await prepararUsuario(usuario);
      ir(papel === 'agenciador' ? '/a?bemvindo=1' : '/m?bemvindo=1', { substituir: true });
    } catch (erro) {
      trocar(lugar,
        faixa([mensagemDoLink(erro), h('small', { class: 'detalhe-erro' }, `Detalhe: ${detalheDoErro(erro)}`)], 'perigo', 'Não deu para entrar'),
        h('button', { class: 'botao cheio', type: 'button', onClick: () => renderizar() }, 'Tentar de novo'));
    }
  }

  (async () => {
    if (sessao.usuario && !entrouSemEmail()) {
      trocar(lugar,
        faixa(`Este link é para o celular ${textos.deQuem}: mande para ele. Para testar neste computador, abra o link numa janela anônima.`,
          'alerta', 'Você já entrou com o seu e-mail'),
        h('a', { class: 'botao cheio', href: '#/inicio' }, 'Voltar para o app'));
      return;
    }
    // Este celular já entrou com este link: segue direto (sem internet também).
    if ((ehMotorista() || ehAgenciador()) && await linkJaUsadoAqui(params.codigo)) {
      ir(ehAgenciador() ? '/a' : '/m', { substituir: true });
      return;
    }
    if (ehIphone() && !abertoPeloIcone() && !sessao.usuario) {
      passosDoIphone(lugar, entrar);
      return;
    }
    await entrar();
  })();
  return pagina;
}

// Celular sem empresa (o acesso foi retirado, ou o motorista foi desligado).
export function telaSemAcesso() {
  return h('div', { class: 'entrada' },
    marca(),
    faixa('Este celular não está mais ligado a nenhuma empresa no HSA Frota. Para voltar, peça um link novo para o dono da frota.',
      'alerta', 'Sem acesso'),
    h('button', {
      class: 'botao texto',
      type: 'button',
      onClick: async () => {
        await sair();
        ir('/entrar', { substituir: true });
      },
    }, 'Sair deste celular'));
}

// ---------------------------------------------------------------------
// Início do motorista
// ---------------------------------------------------------------------
function botaoSos() {
  return h('a', { class: 'botao-sos', href: '#/sos' },
    icone('alerta', { tamanho: 30 }),
    h('span', null, h('strong', null, 'SOS mecânica'), h('small', null, 'Problema no caminhão? Ache uma oficina perto.')));
}

function atalhosDoMotorista(viagemId = null) {
  const extra = viagemId ? `&viagem=${viagemId}` : '';
  return h('div', { class: 'atalhos dois' },
    h('a', { class: 'botao', href: `#/m/despesas/nova?tipo=diesel${extra}`, 'aria-label': '+ Diesel' }, icone('bomba', { tamanho: 26 }), 'Diesel'),
    h('a', { class: 'botao', href: `#/m/despesas/nova?${extra.slice(1)}`, 'aria-label': '+ Despesa' }, icone('recibo', { tamanho: 26 }), 'Despesa'));
}

function cartaoVale(l, envio) {
  return h('div', { class: 'cartao' },
    h('div', { class: 'cartao-corpo' },
      h('span', { class: 'cartao-titulo' }, `${dinheiro(l.valor)} · ${data(l.data)}`),
      l.descricao && h('span', { class: 'cartao-linha' }, l.descricao),
      h('span', { class: 'cartao-emblemas' },
        l.assinatura ? emblema('Assinado', 'ok') : emblema('Sem assinatura'),
        l.acerto_id && emblema('No acerto'),
        emblemaEnvio(envio?.get(l.id)))));
}

// Saldo até hoje, numa frase: "A receber: R$ 1.200,00".
export function textoDoSaldo(aReceber) {
  if (Math.round(aReceber * 100) === 0) return { titulo: 'Saldo zerado', tipo: '' };
  return aReceber > 0
    ? { titulo: `A receber: ${dinheiro(aReceber)}`, tipo: 'ok' }
    : { titulo: `Recebeu a mais: ${dinheiro(-aReceber)}`, tipo: 'alerta' };
}

async function meuSaldo() {
  const id = sessao.colaborador;
  const [dados, dele] = await Promise.all([dadosDaPrevia(id), acertos.doColaborador(id)]);
  return saldoDoMotorista({ colaboradorId: id, ate: hojeDaEmpresa(), ...dados, acertos: dele });
}

export function dicaDeInstalar() {
  if (abertoPeloIcone()) return null;
  return ehIphone()
    ? 'Para achar o app depois: no Safari, toque em Compartilhar e em "Adicionar aos Favoritos".'
    : 'Para abrir mais rápido, instale o app: no Chrome, toque nos três pontinhos e em "Instalar app" (ou "Adicionar à tela inicial").';
}

export function telaMotoristaInicio({ busca }) {
  const corpo = h('div', { class: 'secao' }, carregando());

  (async () => {
    const [eu, lista, frota, cargas, meusVales, envio, envioVales, saldo, paradas] = await Promise.all([
      sessao.colaborador ? colaboradores.obter(sessao.colaborador) : null,
      viagens.listar(), veiculos.listar(), fretes.listar(), vales.listar(), situacoesDeEnvio('viagens'), situacoesDeEnvio('lancamentos'),
      sessao.colaborador ? meuSaldo() : null, entregas.listar(),
    ]);
    const avisoManutencao = await avisosDoMotorista(lista).catch(() => null);
    const pessoas = eu ? [eu] : [];
    const emViagem = lista.filter((v) => v.status === 'aberta');
    // O caminhão da viagem dele agora: carregado (com o botão da entrega) ou vazio.
    const hoje = hojeDaEmpresa();
    const fuso = sessao.empresa?.fuso_horario;
    const idsEmViagem = new Set(emViagem.map((v) => v.id));
    const situacoes = situacaoDosCaminhoes({ veiculos: frota, viagens: lista, fretes: cargas, entregas: paradas }, { hoje })
      .filter((x) => x.viagem && idsEmViagem.has(x.viagem.id));
    const situacaoDaViagem = new Map(situacoes.map((x) => [x.viagem.id, { estado: x.estado, texto: textoDaSituacao(x, { fuso }) }]));
    const recentes = meusVales.slice(0, 3);
    const resumo = saldo && textoDoSaldo(saldo.aReceber);
    trocar(corpo,
      botaoSos(),
      avisoManutencao,
      situacoes.map((x) => cartaoDoCaminhaoAgora(x, { paradas, hoje, fuso })),
      motoristaGrava() && atalhosDoMotorista(emViagem.length === 1 ? emViagem[0].id : null),
      cartaoLink({ href: '#/m/raiox', simbolo: 'rota', titulo: 'Raio-X do frete', linhas: ['Antes de aceitar: piso da ANTT, custo, quanto pedir e o que tem de volta'] }),
      saldo && cartaoLink({
        href: '#/m/saldo',
        simbolo: 'dinheiro',
        titulo: resumo.titulo,
        linhas: [`Saldo até hoje (${data(saldo.ate)}), pela prévia do acerto`],
        emblemas: [saldo.acertosNaoPagos.length > 0 && emblema('Acerto a pagar', 'alerta')],
      }),
      emViagem.length > 0
        ? secao('Em viagem agora', h('div', { class: 'lista' },
          emViagem.map((v) => cartaoViagem(v, { frota, pessoas, cargas, envio, base: '#/m/viagens', situacao: situacaoDaViagem.get(v.id) }))), { contador: String(emViagem.length) })
        : h('p', { class: 'pequeno' }, 'Nenhuma viagem em andamento no seu nome. Quando o escritório lançar, ela aparece aqui.'),
      secao('Últimos vales', h('div', { class: 'lista' },
        recentes.length === 0 ? h('p', { class: 'pequeno' }, 'Nenhum vale ainda.') : recentes.map((l) => cartaoVale(l, envioVales)),
        meusVales.length > 0 && h('a', { class: 'botao texto', href: '#/m/saldo' }, 'Ver o saldo e todos os vales'))),
      h('div', { class: 'acoes' },
        h('a', { class: 'botao secundario cheio', href: '#/m/viagens' }, 'Todas as minhas viagens'),
        h('a', { class: 'botao texto', href: '#/ajuda' }, 'Ajuda'),
        h('button', {
          class: 'botao texto',
          type: 'button',
          onClick: async () => {
            const ok = await confirmar({
              titulo: 'Sair deste celular?',
              texto: 'Para entrar de novo, você vai precisar de um link novo do dono da frota.',
              sim: 'Sair',
              perigo: true,
            });
            if (!ok) return;
            await sair();
            ir('/entrar', { substituir: true });
          },
        }, 'Sair deste celular')));
  })().catch((erro) => {
    console.error(erro);
    trocar(corpo, faixa(mensagemDeErro(erro), 'perigo'));
  });

  return casca({
    titulo: nomeDaEmpresa(),
    conteudo: [
      busca.get('bemvindo') && faixa(dicaDeInstalar() ?? 'Tudo certo: é só usar.', 'ok', 'Pronto! Este celular está ligado ao HSA Frota'),
      !contaGravavel() && faixa('A conta da empresa está só para consulta: dá para ver tudo, mas não lançar despesas agora.', 'alerta'),
      corpo,
    ],
  });
}

// ---------------------------------------------------------------------
// Viagens do motorista
// ---------------------------------------------------------------------
export function telaMotoristaViagens() {
  const corpo = h('div', { class: 'secao' }, carregando());
  (async () => {
    const [eu, lista, frota, cargas, envio] = await Promise.all([
      sessao.colaborador ? colaboradores.obter(sessao.colaborador) : null,
      viagens.listar(), veiculos.listar(), fretes.listar(), situacoesDeEnvio('viagens'),
    ]);
    const pessoas = eu ? [eu] : [];
    const cartao = (v) => cartaoViagem(v, { frota, pessoas, cargas, envio, base: '#/m/viagens' });
    const abertas = lista.filter((v) => v.status === 'aberta');
    const outras = lista.filter((v) => v.status !== 'aberta' && v.status !== 'cancelada').slice(0, 30);
    trocar(corpo,
      lista.length === 0 && vazio({ simbolo: 'rota', titulo: 'Nenhuma viagem ainda', texto: 'As viagens que o escritório lançar no seu nome aparecem aqui.' }),
      abertas.length > 0 && secao('Em andamento', h('div', { class: 'lista' }, abertas.map(cartao)), { contador: String(abertas.length) }),
      outras.length > 0 && secao('Anteriores', h('div', { class: 'lista' }, outras.map(cartao))));
  })().catch((erro) => trocar(corpo, faixa(mensagemDeErro(erro), 'perigo')));
  return casca({ titulo: 'Minhas viagens', conteudo: corpo });
}

// O que ele ganha na carga: "Comissão: R$ 1.014,00 · prevista".
function blocoDosGanhos({ situacao, itens }) {
  if (situacao === 'importada') return h('span', { class: 'cartao-linha' }, 'Acertada nos programas antigos.');
  if (itens.length === 0) return h('span', { class: 'cartao-linha' }, 'Sem comissão nesta carga.');
  return h('div', { class: 'ganhos-da-carga' },
    itens.map((item) => {
      const { titulo, detalhe } = textoDoGanho(item);
      return h('p', null,
        h('strong', null, `${titulo === 'Comissão' ? 'Sua comissão' : titulo}: ${dinheiro(item.valor)}`),
        h('small', null, [detalhe, situacao === 'acertada' ? 'já acertada' : 'prevista'].filter(Boolean).join(' · ')));
    }));
}

function linhaDaCarga(f, { cliente, ganhos, envio, paradas = [], hoje, semBotao = false }) {
  const de = [f.origem_cidade, f.origem_uf].filter(Boolean).join('/');
  const para = [f.destino_cidade, f.destino_uf].filter(Boolean).join('/');
  const mexe = motoristaGrava() && CAMPOS_DA_CARGA.some((c) => motoristaMexe(f, c));
  const falta = faltaDadoNaCarga(f);
  // A descarga em branco não é dado faltando: o botão "Carga entregue" preenche.
  const faltaAlemDaEntrega = CAMPOS_DA_CARGA.some((c) => c !== 'data_descarga' && (f[c] === null || f[c] === undefined || f[c] === ''));
  return h('div', { class: 'cartao carga-do-motorista' },
    h('div', { class: 'cartao-corpo' },
      h('span', { class: 'cartao-titulo' }, `${f.complemento_de ? 'Complemento · ' : ''}${dinheiro(f.valor_frete)} · ${data(f.data_carregamento)}`),
      (de || para) && h('span', { class: 'cartao-linha' }, paradas.length > 0 ? textoDaRota(f, paradas) : `${de || '?'} → ${para || '?'}`),
      blocoDasEntregas(f, { paradas, hoje, semBotao }),
      (f.mercadoria || f.peso_kg) && h('span', { class: 'cartao-linha' },
        [f.mercadoria, f.peso_kg ? `${numeroBR(f.peso_kg)} kg` : null].filter(Boolean).join(' · ')),
      f.cte_numero && h('span', { class: 'cartao-linha' }, `CT-e / Manifesto: ${f.cte_numero}`),
      f.data_descarga && h('span', { class: 'cartao-linha' }, `Descarga: ${data(f.data_descarga)}`),
      cliente && h('span', { class: 'cartao-linha' }, cliente.nome),
      blocoDosGanhos(ganhos),
      h('span', { class: 'cartao-emblemas' },
        f.data_descarga ? emblema('Entregue', 'ok') : f.data_carregamento <= hoje && emblema('No caminhão', 'info'),
        faltaAlemDaEntrega && emblema('Faltam dados', 'alerta'),
        emblemaEnvio(envio?.get(f.id))),
      mexe && h('a', { class: 'botao secundario', href: `#/m/cargas/${f.id}` }, falta ? 'Completar os dados' : 'Corrigir os dados')));
}

export function telaMotoristaViagem({ params }) {
  const tela = nestaTela();
  const corpo = h('div', { class: 'secao' }, carregando());
  const titulo = h('span', null, 'Viagem');
  (async () => {
    const v = await viagens.obter(params.id);
    if (!v) {
      trocar(corpo, vazio({ simbolo: 'rota', titulo: 'Viagem não encontrada', acao: h('a', { class: 'botao', href: '#/m/viagens' }, 'Ver as viagens') }));
      return;
    }
    const [frota, cargas, listaClientes, meusVales, todasDespesas, cats, envio, envioCargas, dados, paradas, eventos] = await Promise.all([
      veiculos.listar(), fretes.daViagem(v.id), clientes.listar(), vales.daViagem(v.id), despesas.daViagem(v.id),
      categorias.todas(), situacoesDeEnvio('lancamentos'), situacoesDeEnvio('fretes'), dadosDaPrevia(sessao.colaborador), entregas.listar(),
      eventosDaViagem.daViagem(v.id),
    ]);
    // No modo viagem (iniciada e não encerrada), a entrega é marcada no "próximo passo".
    const noModoViagem = v.status === 'aberta' && eventos.some((e) => e.tipo === 'inicio') && !eventos.some((e) => e.tipo === 'fim');
    const hoje = hojeDaEmpresa();
    const ganhos = (f) => ganhosDaCarga(f, { viagem: v, regras: dados.regras, lancamentos: dados.lancamentos, colaboradorId: sessao.colaborador });
    const caminhao = frota.find((x) => x.id === v.veiculo_id);
    titulo.textContent = `Viagem · ${caminhao ? textoCaminhao(caminhao).split(' · ')[0] : ''}`;
    const [statusTexto, statusTipo] = STATUS[v.status] ?? [v.status, ''];
    const nomeCat = (id) => cats.find((c) => c.id === id)?.nome ?? 'Despesa';
    const podeLancar = motoristaGrava() && v.status === 'aberta';
    trocar(corpo,
      h('div', { class: 'cartao destaque' }, h('div', { class: 'cartao-corpo' },
        h('span', { class: 'cartao-titulo' }, textoCaminhao(caminhao)),
        h('span', { class: 'cartao-linha' }, v.data_chegada ? `De ${data(v.data_saida)} a ${data(v.data_chegada)}` : `Saiu em ${data(v.data_saida)}`),
        h('span', { class: 'cartao-emblemas' }, emblema(statusTexto, statusTipo)))),
      // Modo viagem (0.23.0): iniciar, o próximo passo, a rota, os gastos, encerrar.
      blocoModoViagem(v, { aoMudar: () => tela.renderizar() }),
      podeLancar && atalhosDoMotorista(v.id),
      secao('Cargas', h('div', { class: 'lista' },
        cargas.length === 0 ? h('p', { class: 'pequeno' }, 'Nenhuma carga lançada ainda.')
          : cargas.map((f) => linhaDaCarga(f, {
            cliente: listaClientes.find((c) => c.id === f.cliente_id), ganhos: ganhos(f), envio: envioCargas, paradas: paradasDaCarga(paradas, f.id), hoje,
            semBotao: noModoViagem,
          }))),
      { contador: String(cargas.length) }),
      secao('Vales', h('div', { class: 'lista' },
        meusVales.length === 0 ? h('p', { class: 'pequeno' }, 'Nenhum vale nesta viagem.') : meusVales.map((l) => cartaoVale(l, envio))),
      { contador: String(meusVales.length) }),
      secao('Despesas', h('div', { class: 'lista' },
        todasDespesas.length === 0 ? h('p', { class: 'pequeno' }, 'Nenhuma despesa nesta viagem.')
          : todasDespesas.map((l) => cartaoDaDespesa(l, nomeCat(l.categoria_id), envio))), { contador: String(todasDespesas.length) }));
  })().catch((erro) => trocar(corpo, faixa(mensagemDeErro(erro), 'perigo')));
  return casca({ titulo, voltar: '/m/viagens', conteudo: corpo });
}

// ---------------------------------------------------------------------
// Dados da carga que o escritório não lançou (CT-e, peso…): o motorista
// preenche o que está vazio e corrige o que ele mesmo preencheu.
// ---------------------------------------------------------------------
export function telaMotoristaCarga({ params }) {
  const tela = nestaTela();
  const corpo = h('div', { class: 'secao' }, carregando());
  let voltar = '/m/viagens';

  (async () => {
    const f = await fretes.obter(params.id);
    if (!f) {
      trocar(corpo, vazio({ simbolo: 'rota', titulo: 'Carga não encontrada', acao: h('a', { class: 'botao', href: '#/m/viagens' }, 'Ver as viagens') }));
      return;
    }
    voltar = `/m/viagens/${f.viagem_id}`;
    const pode = (nome) => motoristaGrava() && motoristaMexe(f, nome);
    const doEscritorio = (nome) => !motoristaMexe(f, nome);
    const dica = (nome) => (doEscritorio(nome) ? 'Lançado pelo escritório.' : null);
    const form = formulario({
      filhos: [
        campo({ rotulo: 'Nº do CT-e / Manifesto', nome: 'cte_numero', valor: f.cte_numero ?? '', desativado: !pode('cte_numero'), dica: dica('cte_numero'),
          atributos: { inputmode: 'numeric', autocomplete: 'off' } }),
        campo({ rotulo: 'Tipo de carga', nome: 'mercadoria', valor: f.mercadoria ?? '', desativado: !pode('mercadoria'), dica: dica('mercadoria'),
          atributos: { autocomplete: 'off', placeholder: 'Ex.: pisos, grãos, bobinas' } }),
        campo({ rotulo: 'Peso (kg)', nome: 'peso_kg', formato: 'km', valor: f.peso_kg != null ? numeroBR(Math.round(f.peso_kg)) : '',
          desativado: !pode('peso_kg'), dica: dica('peso_kg'), atributos: { inputmode: 'numeric' } }),
        campo({ rotulo: 'Data da descarga', nome: 'data_descarga', tipo: 'date', valor: f.data_descarga ?? '', desativado: !pode('data_descarga'),
          dica: dica('data_descarga') }),
        h('div', { class: 'grade-uf' },
          campo({ rotulo: 'Origem (cidade)', nome: 'origem_cidade', formato: 'nome', valor: f.origem_cidade ?? '', desativado: !pode('origem_cidade') }),
          campo({ rotulo: 'UF', nome: 'origem_uf', tipo: 'select', valor: f.origem_uf ?? '', opcoes: opcoesUf, desativado: !pode('origem_uf') })),
        h('div', { class: 'grade-uf' },
          campo({ rotulo: 'Destino (cidade)', nome: 'destino_cidade', formato: 'nome', valor: f.destino_cidade ?? '', desativado: !pode('destino_cidade') }),
          campo({ rotulo: 'UF', nome: 'destino_uf', tipo: 'select', valor: f.destino_uf ?? '', opcoes: opcoesUf, desativado: !pode('destino_uf') })),
      ],
      textoBotao: 'Salvar os dados',
      somenteLeitura: !motoristaGrava() || !CAMPOS_DA_CARGA.some((c) => pode(c)),
      validar: (d) => {
        const erros = {};
        if (d.peso_kg && !(lerInteiro(d.peso_kg) >= 0)) erros.peso_kg = 'Só números, como 27.000.';
        if (d.data_descarga && d.data_descarga < f.data_carregamento) {
          erros.data_descarga = `A carga foi carregada em ${data(f.data_carregamento)}: a descarga não pode ser antes.`;
        }
        return erros;
      },
      aoEnviar: async (d) => {
        // Só os campos abertos para ele (os do escritório nem vão).
        const valores = {
          cte_numero: d.cte_numero?.trim() || null,
          mercadoria: d.mercadoria?.trim() || null,
          peso_kg: d.peso_kg ? lerInteiro(d.peso_kg) : null,
          data_descarga: d.data_descarga || null,
          // A hora do "Carga entregue" vale enquanto o dia da descarga não muda.
          entregue_em: (d.data_descarga || null) === f.data_descarga ? f.entregue_em ?? null : null,
          origem_cidade: d.origem_cidade?.trim() || null,
          origem_uf: d.origem_uf || null,
          destino_cidade: d.destino_cidade?.trim() || null,
          destino_uf: d.destino_uf || null,
        };
        const dados = Object.fromEntries(Object.entries(valores).filter(([nome]) => pode(nome)));
        await fretes.atualizar(f.id, dados, f);
        avisar('Dados da carga salvos.');
        tela.ir(voltar, { substituir: true });
      },
    });
    // O número pelo código de barras do DACTE (0.17.0)
    const campoCte = form.querySelector('[name="cte_numero"]');
    if (campoCte && pode('cte_numero')) campoCte.closest('.campo').after(leitorDaChave({ campoNumero: campoCte }));
    trocar(corpo,
      h('div', { class: 'cartao destaque' }, h('div', { class: 'cartao-corpo' },
        h('span', { class: 'cartao-titulo' }, `${dinheiro(f.valor_frete)} · carregada em ${data(f.data_carregamento)}`),
        h('span', { class: 'cartao-linha' }, 'Complete o que o escritório ainda não lançou. O escritório vê e pode corrigir.'))),
      form);
  })().catch((erro) => {
    console.error(erro);
    trocar(corpo, faixa(mensagemDeErro(erro), 'perigo'));
  });

  const pagina = casca({ titulo: 'Dados da carga', voltar: '/m/viagens', conteudo: corpo });
  // O "voltar" leva à viagem da carga.
  pagina.querySelector('.topo a[aria-label="Voltar"]')?.addEventListener('click', (e) => {
    e.preventDefault();
    ir(voltar);
  });
  return pagina;
}

// ---------------------------------------------------------------------
// Saldo e vales: a prévia do acerto até hoje, os acertos e os vales
// ---------------------------------------------------------------------
export function linhaDeValor(titulo, valor, detalhe = null) {
  return h('div', { class: 'cartao' }, h('div', { class: 'cartao-corpo linha-valor' },
    h('span', { class: 'cartao-titulo' }, h('span', null, titulo), h('strong', null, dinheiro(valor))),
    detalhe && h('span', { class: 'cartao-linha' }, detalhe)));
}

export function quitacaoDoAcerto(a) {
  const saldo = Number(a.saldo);
  if (a.status === 'fechado') {
    if (Math.round(saldo * 100) === 0) return 'Zerado';
    return saldo < 0 ? `A empresa vai te pagar ${dinheiro(-saldo)}` : `Você devolve ${dinheiro(saldo)}`;
  }
  const quando = a.quitado_em ? ` em ${data(a.quitado_em)}` : '';
  switch (a.quitacao) {
    case 'paga': return `A empresa pagou ${dinheiro(-saldo)}${quando}`;
    case 'devolvida': return `Você devolveu ${dinheiro(saldo)}${quando}`;
    case 'transportada': return `Saldo de ${dinheiro(Math.abs(saldo))} passou para o acerto seguinte`;
    default: return 'Zerado';
  }
}

export function telaMotoristaSaldo() {
  const corpo = h('div', { class: 'secao' }, carregando());
  (async () => {
    const id = sessao.colaborador;
    const [saldo, todosVales, dele, cats] = await Promise.all([meuSaldo(), vales.listar(), acertos.doColaborador(id), categorias.todas()]);
    const nomeCat = (cid) => cats.find((c) => c.id === cid)?.nome ?? 'Despesa';
    const resumo = textoDoSaldo(saldo.aReceber);
    const t = saldo.totais;
    const daPrevia = new Set(saldo.vales.map((l) => l.id));
    const valesAcertados = todosVales.filter((l) => !daPrevia.has(l.id) && l.acerto_id).slice(0, 20);
    const numero = (acertoId) => {
      const a = dele.find((x) => x.id === acertoId);
      return a ? `acerto nº ${a.numero}/${a.ano}` : 'acertado';
    };
    trocar(corpo,
      h('div', { class: 'cartao destaque' }, h('div', { class: 'cartao-corpo' },
        h('span', { class: 'valor-grande' }, resumo.titulo),
        h('span', { class: 'cartao-linha' }, `Conta até hoje (${data(saldo.ate)}). É uma prévia: o valor certo sai no acerto, que o escritório confere.`))),
      saldo.acertosNaoPagos.length > 0 && secao('Acerto fechado, ainda não pago', h('div', { class: 'lista' },
        saldo.acertosNaoPagos.map((a) => linhaDeValor(`Acerto nº ${a.numero}/${a.ano} · ${data(a.data)}`, Math.abs(Number(a.saldo)), quitacaoDoAcerto(a))))),
      secao('Desde o último acerto', h('dl', { class: 'linha-dados resumo' },
        t.saldoAnterior !== 0 && [h('dt', null, 'Saldo do acerto anterior'), h('dd', null, dinheiro(-t.saldoAnterior))],
        h('dt', null, '+ Seus ganhos'), h('dd', null, dinheiro(t.creditos)),
        h('dt', null, '+ Despesas que você pagou'), h('dd', null, dinheiro(t.despesas)),
        h('dt', null, '− Vales que você recebeu'), h('dd', null, dinheiro(t.vales)),
        h('dt', null, '− Descontos'), h('dd', null, dinheiro(t.descontos)),
        h('dt', { class: 'total' }, '= Saldo'), h('dd', { class: 'total' }, dinheiro(-t.saldo))),
      saldo.ultimoAcerto ? { contador: `desde o nº ${saldo.ultimoAcerto.numero}/${saldo.ultimoAcerto.ano}` } : undefined),
      secao('Seus ganhos', h('div', { class: 'lista' },
        saldo.ganhos.length === 0 ? h('p', { class: 'pequeno' }, 'Nada ainda desde o último acerto.')
          : saldo.ganhos.map((g) => {
            // "Comissão de 13% sobre R$ 7.800,00 (frete bruto) · carga de 05/10/2026"
            const [primeira, resto] = String(g.descricao ?? '').split(' · ');
            return linhaDeValor(primeira || textoDoGanho(g).titulo, g.valor, resto || (g.id && g.data ? data(g.data) : null));
          })),
      { contador: String(saldo.ganhos.length) }),
      secao('Vales que você recebeu', h('div', { class: 'lista' },
        saldo.vales.length === 0 ? h('p', { class: 'pequeno' }, 'Nenhum vale desde o último acerto.')
          : saldo.vales.map((l) => linhaDeValor(`Vale de ${data(l.data)}`, l.valor, l.descricao))),
      { contador: String(saldo.vales.length) }),
      saldo.despesas.length > 0 && secao('Despesas que você pagou', h('div', { class: 'lista' },
        saldo.despesas.map((l) => linhaDeValor(`${nomeCat(l.categoria_id)} · ${data(l.data)}`, l.valor, 'Volta para você no acerto')))),
      saldo.descontos.length > 0 && secao('Descontos', h('div', { class: 'lista' },
        saldo.descontos.map((l) => linhaDeValor(`${l.descricao || 'Desconto'} · ${data(l.data)}`, l.valor)))),
      valesAcertados.length > 0 && secao('Vales já acertados', h('div', { class: 'lista' },
        valesAcertados.map((l) => linhaDeValor(`Vale de ${data(l.data)}`, l.valor, numero(l.acerto_id))))),
      dele.length > 0 && secao('Acertos', h('div', { class: 'lista' },
        dele.slice(0, 12).map((a) => linhaDeValor(`Nº ${a.numero}/${a.ano} · ${data(a.data)}`, Math.abs(Number(a.saldo)), quitacaoDoAcerto(a))))));
  })().catch((erro) => {
    console.error(erro);
    trocar(corpo, faixa(mensagemDeErro(erro), 'perigo'));
  });
  return casca({ titulo: 'Saldo e vales', conteudo: corpo });
}

// ---------------------------------------------------------------------
// Despesas do motorista
// ---------------------------------------------------------------------
function cartaoDaDespesa(l, nomeCat, envio) {
  const minha = l.created_by === sessao.usuario?.id || !l.created_by;
  const linhas = [
    l.pago_por === 'colaborador' ? 'Você pagou · volta no acerto' : 'A empresa pagou',
    l.litros ? `${formatarLitros(l.litros)} L${l.hodometro != null ? ` · km ${numeroBR(l.hodometro)}` : ''}` : null,
    l.sem_nota_motivo ? `Sem nota: ${l.sem_nota_motivo}` : null,
  ];
  const emblemas = [l.acerto_id && emblema('No acerto', 'ok'), !minha && emblema('Lançada pelo escritório'), emblemaEnvio(envio?.get(l.id))];
  return cartaoLink({
    href: `#/m/despesas/${l.id}`,
    simbolo: l.litros ? 'bomba' : 'recibo',
    titulo: `${dinheiro(l.valor)} · ${nomeCat} · ${data(l.data)}`,
    linhas,
    emblemas,
  });
}

export function telaMotoristaDespesas() {
  const corpo = h('div', { class: 'secao' }, carregando());
  (async () => {
    const [lista, cats, envio] = await Promise.all([despesas.listar(), categorias.todas(), situacoesDeEnvio('lancamentos')]);
    const nomeCat = (id) => cats.find((c) => c.id === id)?.nome ?? 'Despesa';
    const recentes = lista.slice(0, 40);
    trocar(corpo,
      motoristaGrava() && atalhosDoMotorista(),
      lista.length === 0
        ? vazio({ simbolo: 'recibo', titulo: 'Nenhuma despesa ainda', texto: 'Lance o diesel e as despesas da viagem com a foto do comprovante. O escritório confere no acerto.' })
        : secao('Despesas', h('div', { class: 'lista' }, recentes.map((l) => cartaoDaDespesa(l, nomeCat(l.categoria_id), envio))),
          { contador: String(lista.length) }));
  })().catch((erro) => trocar(corpo, faixa(mensagemDeErro(erro), 'perigo')));
  return casca({ titulo: 'Minhas despesas', conteudo: corpo });
}

export function telaMotoristaDespesa({ params, busca }) {
  const tela = nestaTela();
  const nova = !params.id;
  const corpo = h('div', { class: 'secao' }, carregando());
  const titulo = h('span', null, nova ? (busca.get('tipo') === 'diesel' ? 'Abastecimento' : 'Nova despesa') : 'Despesa');

  (async () => {
    const despesa = nova ? null : await despesas.obter(params.id);
    if (!nova && !despesa) {
      trocar(corpo, vazio({ simbolo: 'recibo', titulo: 'Despesa não encontrada', acao: h('a', { class: 'botao', href: '#/m/despesas' }, 'Ver as despesas') }));
      return;
    }
    const [cats, todasCats, frota, lista, fotos, envioAnexos, itensDoPlano] = await Promise.all([
      categorias.listar(), categorias.todas(), veiculos.listar(), viagens.listar(),
      despesa ? anexos.doLancamento(despesa.id) : [], situacoesDeEnvio('anexos'), manutencao.listar(),
    ]);
    // Manutenção preventiva feita nesta despesa (o plano do caminhão volta a contar).
    const jaMarcados = despesa?.manutencao_itens ?? [];
    const feita = blocoManutencaoFeita({ marcados: jaMarcados, aoMudar: () => ajustar() });
    const hoje = hojeDaEmpresa();
    const catPorId = new Map(todasCats.map((c) => [c.id, c]));
    // Lançada aqui e ainda sem resposta do servidor: sem autor ainda, mas é dele.
    const minha = !despesa || !despesa.created_by || despesa.created_by === sessao.usuario?.id;
    const gravavel = motoristaGrava() && minha && !despesa?.acerto_id;
    const diesel = cats.find((c) => c.codigo === 'diesel') ?? cats.find((c) => c.exige_hodometro);
    const tratores = frota.filter((v) => v.tipo === 'trator' && (v.ativo || v.id === despesa?.veiculo_id));
    const minhas = lista.filter((v) => v.status !== 'cancelada');
    const opcoesViagem = [
      ...minhas.filter((v) => v.status === 'aberta'),
      ...minhas.filter((v) => v.status !== 'aberta').slice(0, 5),
    ];
    if (despesa?.viagem_id && !opcoesViagem.some((v) => v.id === despesa.viagem_id)) {
      const atual = lista.find((v) => v.id === despesa.viagem_id);
      if (atual) opcoesViagem.push(atual);
    }
    const textoViagem = (v) => `${textoCaminhao(frota.find((x) => x.id === v.veiculo_id))} · ${data(v.data_saida)}${v.status === 'aberta' ? ' (em andamento)' : ''}`;
    const pedida = busca.get('viagem');
    const viagemInicial = despesa ? despesa.viagem_id ?? '' : pedida ?? (minhas.find((v) => v.status === 'aberta')?.id ?? '');
    // `?categoria=<id>`: a categoria já escolhida (o "+ Pedágio" do modo viagem).
    const catInicial = despesa?.categoria_id ?? (busca.get('tipo') === 'diesel' ? diesel?.id ?? '' : busca.get('categoria') ?? '');

    const campoKmConfirmado = campo({ rotulo: 'Confirmo: a leitura está certa', nome: 'hodometro_confirmado', tipo: 'caixa', valor: despesa?.hodometro_confirmado ?? false });
    const textoKm = h('p', null);
    const blocoKm = h('div', { class: 'aviso-km', role: 'status', hidden: true }, h('strong', null, 'Confira o km do painel'), textoKm, campoKmConfirmado);
    const campoLitros = campo({
      rotulo: 'Litros', nome: 'litros', formato: 'litros', valor: despesa?.litros != null ? formatarLitros(despesa.litros) : '',
      atributos: { inputmode: 'decimal' },
    });
    const campoKm = campo({
      rotulo: 'Km no painel', nome: 'hodometro', formato: 'km', valor: despesa?.hodometro != null ? numeroBR(despesa.hodometro) : '',
      atributos: { inputmode: 'numeric' },
    });
    const campoCaminhao = campo({
      rotulo: 'Caminhão', nome: 'veiculo_id', tipo: 'select', valor: despesa?.veiculo_id ?? (tratores.length === 1 ? tratores[0].id : ''),
      opcoes: [['', 'Escolha o caminhão'], ...tratores.map((v) => [v.id, textoCaminhao(v)])],
      aoMudar: () => ajustar(),
    });

    const fotosNovas = [];
    const gradeFotos = h('div', { class: 'fotos' });
    const desenharFotos = () => trocar(gradeFotos,
      fotos.map((a) => miniaturaDoAnexo(a, { esperando: Boolean(envioAnexos.get(a.id)) })),
      fotosNovas.map((foto) => miniaturaDaPrevia(foto, {
        aoRemover: () => {
          fotosNovas.splice(fotosNovas.indexOf(foto), 1);
          desenharFotos();
          lerNota.atualizar();
        },
      })));
    desenharFotos();

    // Leitura da nota com IA: preenche o que está em branco com o que leu na foto.
    const lerNota = botaoDeLerNota({
      fotos: () => fotosNovas,
      atuais: () => ({
        valor: valorDe('valor'), data: valorDe('data'), hoje, litros: valorDe('litros'),
        fornecedor: valorDe('fornecedor'), categoria: valorDe('categoria_id'),
      }),
      categorias: () => cats,
      preencher: (p) => {
        const por = (nome, valor) => {
          const el = form.querySelector(`[name="${nome}"]`);
          if (el) el.value = valor;
        };
        if (p.valor) por('valor', numeroBR(p.valor, { casas: 2 }));
        if (p.data) por('data', p.data);
        if (p.fornecedor) por('fornecedor', p.fornecedor);
        if (p.categoria) por('categoria_id', p.categoria);
        if (p.litros) por('litros', formatarLitros(p.litros));
        ajustar();
      },
    });

    // Sem nota (chapa, lavação…): o motivo, da lista ou escrito.
    const motivoAntes = despesa?.sem_nota_motivo ?? null;
    const daLista = MOTIVOS_SEM_NOTA.some(([v]) => v === motivoAntes && v !== 'outro');
    const campoSemNota = campo({
      rotulo: 'Esta despesa não tem nota', nome: 'sem_nota', tipo: 'caixa', valor: Boolean(motivoAntes), aoMudar: () => ajustar(),
    });
    const campoMotivo = campo({
      rotulo: 'Por que não tem nota?', nome: 'motivo_sem_nota', tipo: 'select', valor: motivoAntes ? (daLista ? motivoAntes : 'outro') : '',
      opcoes: [['', 'Escolha o motivo'], ...MOTIVOS_SEM_NOTA], aoMudar: () => ajustar(),
    });
    const campoMotivoOutro = campo({
      rotulo: 'Qual o motivo?', nome: 'motivo_outro', valor: motivoAntes && !daLista ? motivoAntes : '', atributos: { maxlength: '120', autocomplete: 'off' },
    });

    const valorDe = (nome) => form.querySelector(`[name="${nome}"]`)?.value ?? '';
    const marcado = (nome) => Boolean(form.querySelector(`[name="${nome}"]`)?.checked);
    function ajustar() {
      const cat = catPorId.get(valorDe('categoria_id'));
      const abastecimento = Boolean(cat?.exige_hodometro);
      const caminhao = lista.find((v) => v.id === valorDe('viagem_id'))?.veiculo_id ?? valorDe('veiculo_id');
      feita.mostrar(caminhao ? itensDoPlano.filter((i) => i.veiculo_id === caminhao) : [], cat?.grupo === 'manutencao' || jaMarcados.length > 0);
      const pedeKm = abastecimento || feita.pedeKm();
      campoLitros.hidden = !(abastecimento || cat?.codigo === 'arla');
      campoKm.hidden = !abastecimento && feita.bloco.hidden;
      rotular(campoKm, pedeKm ? 'Km no painel' : 'Km no painel (opcional)', pedeKm);
      campoCaminhao.hidden = Boolean(valorDe('viagem_id'));
      campoMotivo.hidden = !marcado('sem_nota');
      campoMotivoOutro.hidden = campoMotivo.hidden || valorDe('motivo_sem_nota') !== 'outro';
      if (nova) titulo.textContent = abastecimento ? 'Abastecimento' : 'Nova despesa';
    }

    const form = formulario({
      filhos: [
        campo({
          rotulo: 'Categoria', nome: 'categoria_id', tipo: 'select', obrigatorio: true, valor: catInicial,
          opcoes: [['', 'Escolha a categoria'], ...opcoesDeCategoria(cats)], aoMudar: () => ajustar(),
        }),
        campo({
          rotulo: 'Viagem', nome: 'viagem_id', tipo: 'select', valor: viagemInicial,
          opcoes: [['', 'Sem viagem'], ...opcoesViagem.map((v) => [v.id, textoViagem(v)])], aoMudar: () => ajustar(),
        }),
        campoCaminhao,
        h('div', { class: 'grade-2' },
          campo({
            rotulo: 'Valor (R$)', nome: 'valor', obrigatorio: true, formato: 'dinheiro',
            valor: despesa ? numeroBR(despesa.valor, { casas: 2 }) : '', atributos: { inputmode: 'decimal' },
          }),
          campo({ rotulo: 'Data', nome: 'data', tipo: 'date', obrigatorio: true, valor: despesa?.data ?? hoje })),
        feita.bloco,
        h('div', { class: 'grade-2' }, campoLitros, campoKm),
        blocoKm,
        campo({
          rotulo: 'Quem pagou', nome: 'pago_por', tipo: 'segmentos', obrigatorio: true, valor: despesa?.pago_por ?? 'colaborador',
          opcoes: [['colaborador', 'Eu paguei', 'volta no acerto'], ['empresa', 'A empresa', 'cartão, tag…']],
        }),
        campo({ rotulo: 'Forma de pagamento', nome: 'forma', tipo: 'select', valor: despesa?.forma ?? '', opcoes: [['', 'Não informar'], ...FORMAS_PAGAMENTO] }),
        campo({ rotulo: 'Posto ou fornecedor', nome: 'fornecedor', valor: despesa?.fornecedor ?? '', atributos: { autocomplete: 'off' } }),
        campo({ rotulo: 'Observação', nome: 'descricao', tipo: 'textarea', valor: despesa?.descricao ?? '' }),
        h('fieldset', { class: 'campo' },
          h('legend', null, 'Foto da nota ou do comprovante'),
          gradeFotos,
          gravavel && botoesDeFoto((novas) => {
            fotosNovas.push(...novas);
            desenharFotos();
            lerNota.atualizar();
          }),
          gravavel && lerNota.botao,
          gravavel && h('p', { class: 'dica' }, 'Fica guardada no celular e sobe quando houver sinal.')),
        campoSemNota,
        campoMotivo,
        campoMotivoOutro,
      ],
      textoBotao: nova ? 'Salvar' : 'Salvar alterações',
      somenteLeitura: !gravavel,
      validar: (d) => {
        const erros = {};
        const cat = catPorId.get(d.categoria_id);
        if (!cat) erros.categoria_id = 'Escolha a categoria.';
        if (!d.viagem_id && !d.veiculo_id) erros.veiculo_id = 'Escolha o caminhão (ou a viagem).';
        if (!(lerNumero(d.valor) > 0)) erros.valor = 'Informe o valor, como 250,00.';
        if (!d.data) erros.data = 'Informe a data.';
        const viagem = lista.find((v) => v.id === d.viagem_id);
        if (viagem && d.data && d.data < viagem.data_saida) erros.data = `A viagem saiu em ${data(viagem.data_saida)}: a despesa não pode ser antes.`;
        if (cat?.exige_hodometro) {
          if (!(lerNumero(d.litros) > 0)) erros.litros = 'Informe os litros.';
          if (!(lerInteiro(d.hodometro) >= 0)) erros.hodometro = 'Informe o km do painel.';
        } else if (feita.pedeKm() && !(lerInteiro(d.hodometro) >= 0)) {
          erros.hodometro = 'Informe o km do painel: a manutenção marcada conta pelo km.';
        }
        if (!d.pago_por) erros.pago_por = 'Diga quem pagou.';
        // A foto da nota, ou o motivo de não ter nota.
        if (d.sem_nota === true) {
          if (!d.motivo_sem_nota) erros.motivo_sem_nota = 'Escolha por que não tem nota.';
          else if (!motivoSemNota(d.motivo_sem_nota, d.motivo_outro)) erros.motivo_outro = 'Escreva o motivo, como "chapa".';
        } else if (fotos.length + fotosNovas.length === 0) {
          erros.sem_nota = 'Tire a foto da nota ou marque que esta despesa não tem nota.';
        }
        return erros;
      },
      aoEnviar: async (d) => {
        const cat = catPorId.get(d.categoria_id);
        const viagem = lista.find((v) => v.id === d.viagem_id) ?? null;
        const km = (cat?.exige_hodometro || !feita.bloco.hidden) && d.hodometro ? lerInteiro(d.hodometro) : null;
        const dados = {
          categoria_id: d.categoria_id,
          viagem_id: viagem?.id ?? null,
          veiculo_id: viagem?.veiculo_id ?? d.veiculo_id,
          valor: lerNumero(d.valor),
          data: d.data,
          litros: (cat?.exige_hodometro || cat?.codigo === 'arla') && d.litros ? lerNumero(d.litros) : null,
          hodometro: km,
          hodometro_confirmado: km !== null && !blocoKm.hidden && d.hodometro_confirmado === true,
          pago_por: d.pago_por,
          colaborador_id: d.pago_por === 'colaborador' ? sessao.colaborador : null,
          forma: d.forma || null,
          fornecedor: d.fornecedor || null,
          descricao: d.descricao || null,
          sem_nota_motivo: d.sem_nota === true ? motivoSemNota(d.motivo_sem_nota, d.motivo_outro) : null,
          manutencao_itens: feita.marcados(),
        };
        try {
          if (nova) {
            await despesas.criar(dados, { fotos: fotosNovas });
            avisar(cat?.exige_hodometro ? 'Abastecimento salvo.' : 'Despesa salva.');
          } else {
            await despesas.atualizar(despesa.id, dados, despesa);
            for (const foto of fotosNovas) await anexos.adicionar({ lancamento_id: despesa.id, dia: dados.data }, foto);
            avisar('Alterações salvas.');
          }
          tela.ir(viagem ? `/m/viagens/${viagem.id}` : '/m/despesas', { substituir: true });
        } catch (erro) {
          // Km fora da sequência: o escritório pode ter lançado outro
          // abastecimento. Mostra a leitura e pede a confirmação.
          if (/^HODOMETRO_(FORA_DA_SEQUENCIA|SALTO)$/.test(erro.regra ?? '')) {
            textoKm.textContent = mensagemDeErro(erro);
            blocoKm.hidden = false;
          }
          throw erro;
        }
      },
      extras: !nova && gravavel && h('button', {
        class: 'botao texto',
        type: 'button',
        onClick: async () => {
          const ok = await confirmar({ titulo: 'Excluir esta despesa?', texto: 'Use só para despesa lançada por engano.', sim: 'Excluir', perigo: true });
          if (!ok) return;
          try {
            await despesas.excluir(despesa.id);
            avisar('Despesa excluída.');
            tela.ir('/m/despesas', { substituir: true });
          } catch (erro) {
            avisar(mensagemDeErro(erro), 'erro');
          }
        },
      }, 'Excluir despesa lançada por engano'),
    });
    ajustar();
    trocar(corpo,
      despesa?.acerto_id && faixa('Esta despesa já entrou num acerto: não muda mais.', 'info'),
      despesa && !minha && faixa('Lançada pelo escritório: só para consulta.', 'info'),
      form);
  })().catch((erro) => {
    console.error(erro);
    trocar(corpo, faixa(mensagemDeErro(erro), 'perigo'));
  });

  return casca({ titulo, voltar: '/m/despesas', conteudo: corpo });
}

// ---------------------------------------------------------------------
// Entregas (0.19.0): o caminhão agora, as paradas no caminho e o botão
// "Carga entregue"
// ---------------------------------------------------------------------
// O botão vem de fora: depois do "await" da confirmação, o evento já não
// tem o currentTarget.
async function executarEntrega(botao, acao, sucesso) {
  botao.disabled = true;
  try {
    await acao();
    avisar(sucesso);
    renderizar();
  } catch (erro) {
    avisar(mensagemDeErro(erro), 'erro');
    botao.disabled = false;
  }
}

// O botão da próxima entrega: a parada no caminho ou a carga inteira.
function botaoDaProximaEntrega(f, paradas, { hoje }) {
  if (!motoristaGrava() || f.data_descarga || f.data_carregamento > hoje) return null;
  const pendente = paradas.find((p) => !p.data_entrega);
  if (pendente) {
    const onde = lugar(pendente.cidade, pendente.uf);
    return h('button', {
      class: 'botao cheio',
      type: 'button',
      onClick: async (evento) => {
        const botao = evento.currentTarget;
        if (!(await confirmar({ titulo: `Entregue em ${onde}?`, texto: 'Marca esta entrega no caminho com a data de hoje. A carga continua no caminhão até o destino final.', sim: 'Entregue' }))) return;
        await executarEntrega(botao, () => paradaEntregue(pendente, { hoje }), `Entrega em ${onde} marcada.`);
      },
    }, `Entregue em ${onde}`);
  }
  if (!motoristaMexe(f, 'data_descarga')) return null;
  const destino = lugar(f.destino_cidade, f.destino_uf);
  return h('button', {
    class: 'botao cheio',
    type: 'button',
    onClick: async (evento) => {
      const botao = evento.currentTarget;
      if (!(await confirmar({ titulo: 'Carga entregue?', texto: `Marca a entrega${destino ? ` em ${destino}` : ''} hoje, agora. O escritório vê na hora.`, sim: 'Carga entregue' }))) return;
      await executarEntrega(botao, () => cargaEntregue(f, { hoje }), 'Carga entregue.');
    },
  }, 'Carga entregue');
}

// O caminhão da viagem dele, no Início.
function cartaoDoCaminhaoAgora(x, { paradas, hoje, fuso }) {
  if (x.estado === 'vazio') {
    const onde = x.lugar?.cidade && x.lugar?.uf ? x.lugar : null;
    return faixa([
      `${textoDaSituacao(x, { fuso }).replace(/^Vazio/, 'O caminhão está vazio')}.`,
      onde && h('a', { class: 'botao texto', href: linkDoRadar(onde) }, `Ver o que sai de ${lugar(onde.cidade, onde.uf)}`),
      onde && h('a', { class: 'botao texto', href: linkDoTrajeto({ origem: onde.cidade, uf: onde.uf, caminhao: x.veiculo.id }) }, 'Planejar o trajeto'),
    ], 'info', 'Caminhão vazio');
  }
  const paradasDela = paradasDaCarga(paradas, x.carga.id);
  return h('div', { class: 'cartao destaque entrega-agora' },
    h('div', { class: 'cartao-corpo' },
      h('span', { class: 'cartao-titulo' }, 'Carga no caminhão'),
      h('span', { class: 'cartao-linha' }, textoDaRota(x.carga, paradasDela) || 'Sem a rota lançada'),
      x.proxima.tipo === 'parada' && h('span', { class: 'cartao-linha' }, `Próxima entrega: ${lugar(x.proxima.cidade, x.proxima.uf)}`),
      x.cargas.length > 1 && h('span', { class: 'cartao-linha' }, `${x.cargas.length} cargas no caminhão (com o complemento)`),
      botaoDaProximaEntrega(x.carga, paradasDela, { hoje })));
}

// O Raio-X com o radar da cidade onde o caminhão ficou (Radar de retorno).
const linkDoRadar = ({ cidade, uf }) => `#/m/raiox?origem=${encodeURIComponent(cidade)}&uf=${encodeURIComponent(uf)}`;

// As paradas da carga, com a data em que entregou, o botão da próxima entrega
// e o de incluir uma parada.
// semBotao: no modo viagem, o botão da entrega fica só no "próximo passo".
function blocoDasEntregas(f, { paradas, hoje, semBotao = false }) {
  const fuso = sessao.empresa?.fuso_horario;
  const pode = motoristaGrava() && !f.data_descarga;
  return h('div', { class: 'entregas-da-carga' },
    paradas.length > 0 && h('ul', { class: 'lista-simples pequeno' }, paradas.map((p) => h('li', null,
      `${lugar(p.cidade, p.uf)}: ${p.data_entrega ? `entregue em ${data(p.data_entrega)}` : 'a entregar'}${p.observacao ? ` (${p.observacao})` : ''}`))),
    f.data_descarga && h('span', { class: 'cartao-linha' },
      `Entregue${f.destino_cidade ? ` em ${lugar(f.destino_cidade, f.destino_uf)}` : ''} ${f.entregue_em ? `em ${textoDoMomento(f.entregue_em, { fuso })}` : `no dia ${data(f.data_descarga)}`}.`),
    !semBotao && botaoDaProximaEntrega(f, paradas, { hoje }),
    pode && f.data_carregamento <= hoje && h('button', {
      class: 'botao texto',
      type: 'button',
      onClick: async (evento) => {
        const botao = evento.currentTarget;
        const nova = await dialogoDeParada();
        if (!nova) return;
        await executarEntrega(botao, () => entregas.criar({ ...nova, frete_id: f.id, ordem: paradas.length + 1 }), `Entrega em ${lugar(nova.cidade, nova.uf)} incluída.`);
      },
    }, '+ Entrega no caminho'));
}

function dialogoDeParada() {
  return dialogo((fechar) => {
    const cidade = h('input', { type: 'text', id: 'parada-cidade', autocomplete: 'off' });
    const uf = h('select', { id: 'parada-uf' }, opcoesUf.map(([v, t]) => h('option', { value: v }, t)));
    const erro = h('p', { class: 'erro-campo', role: 'alert' });
    return [
      h('h2', null, 'Entrega no caminho'),
      h('p', null, 'Uma cidade em que parte da carga é entregue antes do destino final.'),
      h('div', { class: 'grade-uf' },
        h('div', { class: 'campo' }, h('label', { for: 'parada-cidade' }, 'Cidade'), cidade),
        h('div', { class: 'campo' }, h('label', { for: 'parada-uf' }, 'UF'), uf)),
      erro,
      h('div', { class: 'acoes' },
        h('button', {
          class: 'botao cheio',
          type: 'button',
          onClick: () => {
            const nome = cidade.value.trim().replace(/\s+/g, ' ');
            if (nome.length < 2 || !uf.value) {
              erro.textContent = 'Informe a cidade e a UF.';
              return;
            }
            fechar({ cidade: nome, uf: uf.value });
          },
        }, 'Incluir a entrega'),
        h('button', { class: 'botao cheio secundario', type: 'button', onClick: () => fechar(null) }, 'Cancelar')),
    ];
  });
}
